import { useBlogTheme } from '@/hooks/useBlogTheme';
import { formatDate, formatReadTime } from '@/lib/utils';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';

const ArticleCard = ({ article }) => {
  const { isBlogLightTheme } = useBlogTheme();

  if (!article) return null;
  return (
    <div
      className={`${
        isBlogLightTheme
          ? 'bg-white border-[#F0F0F0]'
          : 'bg-[#071112] border-[#0C3438]'
      } border relative rounded-[24px] w-full min-h-[494px] group overflow-hidden px-4 py-6 `}
    >
      <Link
        href={`/blog/${article.slug}`}
        className="z-10 absolute inset-0"
        aria-label={`Read article: ${article.name}`}
      />
      <div className="relative rounded-[10px] w-full h-[252px] overflow-hidden">
        <Image
          src={`${process.env.NEXT_PUBLIC_API_URL}/assets/${article.banner}`}
          alt={article.name || 'Article Banner'}
          fill
          className="object-cover"
          loading="lazy"
          sizes="(max-width: 640px) 85vw, (max-width: 768px) 60vw, 463px"
          quality={75}
        />
      </div>
      <div className="flex flex-col pt-4">
        <h3
          className={`font-instrument-serif text-[20px] line-clamp-2 leading-7 ${
            isBlogLightTheme ? 'text-black' : 'text-white'
          }`}
        >
          {article.name}
        </h3>
        <Link
          href={`/blog/${article.slug}`}
          className="flex items-center gap-2 mt-2 font-inter-tight text-[#1FBCCB] text-[14px] group-hover:underline"
          aria-label={`Read more about ${article.name}`}
        >
          Read more
          <TiltedArrow color={isBlogLightTheme ? 'black' : 'white'} />
        </Link>

        <span
          className={`opacity-60 my-6 border-b w-full h-[1px] ${
            isBlogLightTheme ? 'border-[#398C94]' : 'border-[#1D464A]'
          }`}
        ></span>

        <div className="flex items-center gap-2 w-full">
          <span className="relative rounded-full w-[36px] h-[36px] overflow-hidden">
            <Image
              src={`${process.env.NEXT_PUBLIC_API_URL}/assets/${article?.author_profile}`}
              alt={article?.auhtor || 'Author'}
              fill
              className="object-cover"
              loading="lazy"
              sizes="36px"
            />
          </span>

          <span className="flex flex-col">
            <p className="font-instrument-serif text-[16px]">
              {article.author}
            </p>
            <span className="flex items-center gap-2">
              <p className="font-inter-tight text-[#999999] text-[12px]">
                {formatDate(article.published_date)}
              </p>
              <span className="bg-[#999999] rounded-full w-[6px] h-[6px]"></span>
              <span className="flex items-center gap-1 font-inter-tight text-[#999999] text-[12px]">
                <Clock />
                <span>{formatReadTime(article.content_read_time)}</span>
              </span>
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};

const TiltedArrow = ({ color }) => {
  return (
    <svg
      width="15"
      height="8"
      viewBox="0 0 15 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14.2613 3.79299C14.4109 3.5609 14.3441 3.25145 14.112 3.10182L10.3299 0.663491C10.0978 0.513862 9.78832 0.580711 9.63869 0.812802C9.48907 1.04489 9.55592 1.35434 9.78801 1.50396L13.1499 3.67137L10.9825 7.03327C10.8329 7.26536 10.8997 7.5748 11.1318 7.72443C11.3639 7.87406 11.6733 7.80721 11.823 7.57512L14.2613 3.79299ZM0.156738 6.47827L0.262317 6.967L13.9466 4.01079L13.8411 3.52206L13.7355 3.03333L0.0511593 5.98955L0.156738 6.47827Z"
        fill={color}
      />
    </svg>
  );
};
const Clock = () => {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.40004 7.66871L10.994 10.2627L10.4287 10.8287L7.60004 8.00004V4.00004H8.40004V7.66871ZM8.00004 14.6667C4.31804 14.6667 1.33337 11.682 1.33337 8.00004C1.33337 4.31804 4.31804 1.33337 8.00004 1.33337C11.682 1.33337 14.6667 4.31804 14.6667 8.00004C14.6667 11.682 11.682 14.6667 8.00004 14.6667ZM8.00004 13.8667C9.55598 13.8667 11.0482 13.2486 12.1484 12.1484C13.2486 11.0482 13.8667 9.55598 13.8667 8.00004C13.8667 6.4441 13.2486 4.95189 12.1484 3.85168C11.0482 2.75147 9.55598 2.13337 8.00004 2.13337C6.4441 2.13337 4.95189 2.75147 3.85168 3.85168C2.75147 4.95189 2.13337 6.4441 2.13337 8.00004C2.13337 9.55598 2.75147 11.0482 3.85168 12.1484C4.95189 13.2486 6.4441 13.8667 8.00004 13.8667Z"
        fill="#999999"
      />
    </svg>
  );
};

export default ArticleCard;
